import { FC, useEffect, useState, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";

import YouTube from "react-youtube";
import MDButton from "components/MDButton";
import SandClock from "assets/Icons/sandClock.svg?react";

import { makeStyles } from "tss-react/mui";
import { styles, getToastStyles } from "./Step2.styles";
import Timer from "components/Timer/Timer";
import { useAppSelector } from "store/hooks";
import { getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { checkTranslationExists } from "utils/step.utils";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";

const TIMER_LENGTH = 1;
const notify = (msg: string, theme?: any) =>
  toast(msg, {
    position: "top-left",
    hideProgressBar: true,
    draggable: true,
    closeButton: false,
    style: { ...getToastStyles(theme), whiteSpace: "pre-line" },
  });

interface StepProps {
  handleUpdateStep: (step: number) => void;
}

type Step1Map = {
  [key: number]: {
    Component: ReactElement;
    nextButtonText: string;
  };
};

const InnerStep1: FC<any> = ({ classes, youTubeOptions, onReady }) => {
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);

  // Dynamically find all text items
  const textItems = useMemo(() => {
    const items: string[] = [];
    const baseKey = `${testTypeLocaleKey}:Step2Screen:text`;
    let i = 1;
    while (checkTranslationExists(baseKey, i, t)) {
        items.push(t(`${baseKey}${i}`));
      i++;
    }
    return items;
  }, [t, testTypeLocaleKey]);

  const onVideoPlayedHandler =  (event: any) => {
    event.target.playVideo();
    
    // Log tutorial video viewed event
    sentryLogMessage({
      message: 'Tutorial video viewed',
      level: 'info',
      tags: {
        component: 'Step2',
        category: 'tutorial_video',
        event_type: SENTRY_EVENT_TYPES.VIEWED_TUTORIAL_VIDEO,
      },
      extra: {
        videoId: t(`${testTypeLocaleKey}:Step2Screen:youtubeVideo`),
        timestamp: new Date().toISOString(),
      },
    });
  };

  return (
    <>
      {textItems.map((text, index) => (
        <div key={index} className={classes.text}>
          <div className={classes.textBullet}>{index + 1}.&nbsp;&nbsp;</div>
          <span>{text}</span>
        </div>
      ))}
      <div className={classes.youTubeContainer}>
        {youTubeOptions ? (
          <YouTube
            iframeClassName={classes.iframeClassName}
            videoId={t(`${testTypeLocaleKey}:Step2Screen:youtubeVideo`)}
            opts={youTubeOptions}
            onReady={onReady}
            onPlay={ onVideoPlayedHandler }
          />
        ) : null}
      </div>
    </>
  );
};
const InnerStep2: FC<any> = ({ classes, setTimerDone }) => {
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);

  return (
    <div className={classes.innerStep2Container}>
      <div className={classes.sandClockContainer}>
        <SandClock />
      <div className={classes.timerText1}>{t(`${testTypeLocaleKey}:Step2Screen:timerText1`)}</div>
      </div>
      <Timer
        timerLength={TIMER_LENGTH}
        timerLocalStorageKey="step2Timer"
        setTimerDone={setTimerDone}
        text={`${testTypeLocaleKey}:Step2Screen:timerText2`}
      />
    </div>
  );
};

const Step2: FC<StepProps> = ({ handleUpdateStep }) => {
  const [youTubeOptions, setYouTubeOptions] = useState();
  const [timerDone, setTimerDone] = useState(false);
  const [step1InnerStep, setStep1InnerStep] = useState(1);
  const { t } = useTranslation();
  const theme = useTheme();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const isModelsLoaded = useAppSelector(state => state.imageRecognition.isModelsLoaded);

  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();

  const onReady = (event: any) => {
    event.target.pauseVideo();
  };

  useEffect(() => {
    const youTubePlayerWidth =
      document.getElementById("Step2Container").clientWidth;
    const options: any = {
      height: 216,
      width: youTubePlayerWidth,
    };
    setYouTubeOptions(options);
  }, []);

  useEffect(() => {
    if (!isModelsLoaded) {
      notify(t(`${testTypeLocaleKey}:Step2Screen:badConnection`), theme);
    }
  }, []);

  const body: Step1Map = useMemo(() => {
    return {
      1: {
        Component: (
          <InnerStep1
            classes={classes}
            youTubeOptions={youTubeOptions}
            onReady={onReady}
          />
        ),
        nextButtonText: t(`${testTypeLocaleKey}:Step2Screen:nextButton1`),
      },
      2: {
        Component: <InnerStep2 classes={classes} setTimerDone={setTimerDone} />,
        nextButtonText: t(`${testTypeLocaleKey}:Step2Screen:nextButton2`),
      },
    };
  }, [classes, youTubeOptions, onReady, testTypeLocaleKey, t, setTimerDone]);

  const handleNext = () => {
    if (step1InnerStep === 1) {
      // start timer
      setStep1InnerStep(2);
    }
    if (step1InnerStep === 2) {
      handleUpdateStep(4);
    }
  };

  return (
    <div id="Step2Container" className={classes.container}>
      <div className={classes.bodyContainer}>
        {body[step1InnerStep].Component}
      </div>

      <MDButton
        style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
        color={"primary"}
        variant="contained"
        type="submit"
        onClick={handleNext}
        disabled={step1InnerStep === 2 && !timerDone}
      >
        {body[step1InnerStep].nextButtonText}
      </MDButton>
    </div>
  );
};

export default Step2;
